import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraActionList } from '@mono/ui-components/captains';
import { SpecraButton, SpecraQuote, SpecraSpinner, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraFormField, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { responseDocumentClient } from '../../lib/connect/clients';
import { relative } from './statusText';
import { containerKey, toContainerRef } from './useOfficeWorkspace';
export function useDocumentComments(container, documentId, enabled = true) {
    return useQuery({
        queryKey: [
            'officeComments',
            containerKey(container),
            documentId
        ],
        enabled: enabled && !!documentId,
        queryFn: ()=>responseDocumentClient.listDocumentComments({
                container: toContainerRef(container),
                documentId,
                includeDone: true
            })
    });
}
export function CommentsRail(props) {
    // Isolate composer state when switching main documents or alternate drafts.
    return /*#__PURE__*/ _jsx(DocumentComments, {
        ...props
    }, props.documentId);
}
function DocumentComments({ container, documentId, sectionAnchor, canComment, onJumpToAnchor, onChanged }) {
    const comments = useDocumentComments(container, documentId);
    const qc = useQueryClient();
    const [filter, setFilter] = useState('section');
    const [text, setText] = useState('');
    const operation = useRef();
    const mutation = useMutation({
        mutationFn: (input)=>{
            const ref = toContainerRef(container);
            if ('commentId' in input) {
                return responseDocumentClient.resolveDocumentComment({
                    container: ref,
                    documentId,
                    commentId: input.commentId,
                    baseVersionId: comments.data?.versionId ?? ''
                });
            }
            if (operation.current?.text !== input.text || operation.current?.anchor !== input.anchor) {
                operation.current = {
                    ...input,
                    id: crypto.randomUUID()
                };
            }
            return responseDocumentClient.addSectionComment({
                container: ref,
                documentId,
                sectionAnchor: input.anchor,
                text: input.text,
                operationId: operation.current.id
            });
        },
        onSuccess: async (res, input)=>{
            if ('text' in input) {
                setText('');
                operation.current = undefined;
            }
            await Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'officeComments',
                        containerKey(container),
                        documentId
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeWorkspace',
                        containerKey(container)
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeVersions',
                        documentId
                    ]
                })
            ]);
            if (res.version?.id) onChanged(res.version.id);
        }
    });
    const all = comments.data?.comments ?? [];
    const byId = new Map(all.map((c)=>[
            c.id,
            c
        ]));
    const children = new Map();
    for (const c of all){
        const parent = c.parentId && byId.has(c.parentId) ? c.parentId : '';
        children.set(parent, [
            ...children.get(parent) ?? [],
            c
        ]);
    }
    const rows = [];
    const seen = new Set();
    const visit = (c, depth, root)=>{
        if (seen.has(c.id)) return;
        seen.add(c.id);
        const anchor = c.anchor || root.anchor;
        const done = c.done || root.done;
        if ((filter === 'resolved' ? done : !done) && (filter !== 'section' || !!sectionAnchor && anchor === sectionAnchor)) {
            rows.push({
                comment: {
                    ...c,
                    anchor,
                    sectionTitle: c.sectionTitle || root.sectionTitle
                },
                depth
            });
        }
        for (const reply of children.get(c.id) ?? [])visit(reply, depth + 1, root);
    };
    for (const root of children.get('') ?? [])visit(root, 0, root);
    // Keep malformed/orphaned threads readable too.
    for (const c of all)if (!seen.has(c.id)) visit(c, 0, c);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)] p-[var(--space-3)]",
        "data-testid": Testids.officeWorkspace.commentsRail,
        children: [
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Filter comments",
                onValueChange: setFilter,
                options: [
                    {
                        value: 'section',
                        label: 'This section'
                    },
                    {
                        value: 'all',
                        label: 'All'
                    },
                    {
                        value: 'resolved',
                        label: 'Resolved'
                    }
                ],
                testId: Testids.officeWorkspace.commentsFilter,
                value: filter
            }),
            comments.isLoading ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading comments"
            }) : null,
            comments.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                tone: "danger",
                children: "Comments could not be loaded. Try Refresh."
            }) : null,
            !comments.isLoading && !comments.isError && rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: filter === 'section' && !sectionAnchor ? 'Pick a section to see its comments.' : 'No comments match this filter.'
            }) : null,
            /*#__PURE__*/ _jsx(SpecraActionList, {
                getItemProps: (item)=>({
                        className: 'flex-col items-stretch [&>span]:whitespace-normal [&>span]:break-words',
                        style: {
                            paddingInlineStart: `${Math.min(rows.find((r)=>r.comment.id === item.id)?.depth ?? 0, 4) * 16}px`
                        }
                    }),
                getItemTestId: ()=>Testids.officeWorkspace.commentItem,
                items: rows.map(({ comment: c })=>({
                        id: c.id,
                        label: c.text.slice(0, 200),
                        meta: `${c.author} · ${relative(new Date(c.date))} · in ${c.sectionTitle}`,
                        description: /*#__PURE__*/ _jsx(SpecraQuote, {
                            size: "sm",
                            children: c.quote
                        }),
                        actions: filter !== 'resolved' ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.officeWorkspace.commentGoTo,
                                    disabled: !c.anchor,
                                    onClick: ()=>onJumpToAnchor(c.anchor),
                                    size: "xs",
                                    variant: "link",
                                    children: "Go to section"
                                }),
                                canComment ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.officeWorkspace.commentResolve,
                                    disabled: mutation.isPending,
                                    onClick: ()=>mutation.mutate({
                                            commentId: c.id
                                        }),
                                    size: "xs",
                                    variant: "secondary",
                                    children: "Resolve"
                                }) : null,
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.officeWorkspace.commentAskAssist,
                                    onClick: ()=>window.dispatchEvent(new CustomEvent('office-workspace-ask-assist', {
                                            detail: {
                                                prompt: `Address this comment from ${c.author}: “${c.text}” on “${c.quote}”.`,
                                                sectionAnchor: c.anchor
                                            }
                                        })),
                                    size: "xs",
                                    variant: "secondary",
                                    children: "Ask Assist to fix"
                                })
                            ]
                        }) : undefined
                    }))
            }),
            mutation.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                tone: "danger",
                children: "The comment could not be updated. Refresh and try again."
            }) : null,
            canComment ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-[var(--space-2)]",
                "data-testid": Testids.officeWorkspace.commentComposer,
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        htmlFor: "workspace-add-comment",
                        label: "Add a comment",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            disabled: !sectionAnchor || mutation.isPending,
                            id: "workspace-add-comment",
                            onChange: (e)=>setText(e.target.value),
                            value: text
                        })
                    }),
                    !sectionAnchor ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Pick a section to add a comment."
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: !sectionAnchor || !text.trim() || mutation.isPending,
                        onClick: ()=>sectionAnchor && mutation.mutate({
                                text,
                                anchor: sectionAnchor
                            }),
                        size: "sm",
                        children: "Comment"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                "data-testid": Testids.officeWorkspace.commentReadOnlyNote,
                size: "meta",
                tone: "muted",
                children: "You can read comments but not add them. An admin controls who can comment."
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Comments update when the document is saved."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.officeWorkspace.commentsRefresh,
                disabled: comments.isFetching,
                onClick: ()=>void comments.refetch(),
                variant: "link",
                children: "Refresh"
            })
        ]
    });
}
